import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useParams, useLocation } from 'react-router-dom';

interface SEOProps {
  title?: string;
  description?: string;
  keywords?: string;
  image?: string;
  type?: string;
  article?: {
    publishedTime?: string;
    modifiedTime?: string;
    author?: string;
    tags?: string[];
  };
}

const SEO: React.FC<SEOProps> = ({
  title,
  description,
  keywords,
  image = '/images/about.jpg',
  type = 'website',
  article
}) => {
  const { lng } = useParams<{ lng: string }>();
  const location = useLocation();
  const currentLanguage = lng || 'en';
  const baseUrl = 'https://algranconpablo.com';
  const currentUrl = `${baseUrl}${location.pathname}`;
  
  
  const defaultTitles = {
    en: 'Pablo Ledesma - Documentary Film Photographer | Marbella, Costa del Sol',
    es: 'Pablo Ledesma - Fotógrafo Documental Analógico | Marbella, Costa del Sol'
  };
  
  const defaultDescriptions = {
    en: 'Documentary film photographer in Marbella capturing authentic moments and cultural stories on analog film. Specializing in documentary photography, fine art prints, and visual storytelling for Costa del Sol.',
    es: 'Fotógrafo documental en Marbella capturando momentos auténticos e historias culturales en película analógica. Especializado en fotografía documental, arte de pared y narrativa visual para Costa del Sol.'
  };

  const pageTitle = title || defaultTitles[currentLanguage as keyof typeof defaultTitles];
  const pageDescription = description || defaultDescriptions[currentLanguage as keyof typeof defaultDescriptions];

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <html lang={currentLanguage} />
      <title>{pageTitle}</title>
      <meta name="description" content={pageDescription} />
      {keywords && <meta name="keywords" content={keywords} />}
      <meta name="author" content="Pablo Ledesma" />
      <meta name="robots" content="index, follow" />
      
      {/* Canonical URL */}
      <link rel="canonical" href={currentUrl} />
      
      {/* Hreflang Tags */}
      <link rel="alternate" hrefLang="en" href={`${baseUrl}${location.pathname.replace(/^\/(en|es)/, '/en')}`} />
      <link rel="alternate" hrefLang="es" href={`${baseUrl}${location.pathname.replace(/^\/(en|es)/, '/es')}`} />
      <link rel="alternate" hrefLang="x-default" href={`${baseUrl}${location.pathname.replace(/^\/(en|es)/, '/en')}`} />
      
      {/* Open Graph */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={currentUrl} />
      <meta property="og:title" content={pageTitle} />
      <meta property="og:description" content={pageDescription} />
      <meta property="og:image" content={`${baseUrl}${image}`} />
      <meta property="og:locale" content={currentLanguage === 'en' ? 'en_US' : 'es_ES'} />
      <meta property="og:locale:alternate" content={currentLanguage === 'en' ? 'es_ES' : 'en_US'} />
      
      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={currentUrl} />
      <meta name="twitter:title" content={pageTitle} />
      <meta name="twitter:description" content={pageDescription} />
      <meta name="twitter:image" content={`${baseUrl}${image}`} />
      
      {/* Article-specific meta tags */}
      {article && (
        <>
          {article.publishedTime && <meta property="article:published_time" content={article.publishedTime} />}
          {article.modifiedTime && <meta property="article:modified_time" content={article.modifiedTime} />}
          {article.author && <meta property="article:author" content={article.author} />}
          {article.tags && article.tags.map(tag => (
            <meta key={tag} property="article:tag" content={tag} />
          ))}
        </>
      )}
    </Helmet>
  );
};

export default SEO;